import { useState } from 'react';
import { outboundAPI } from '../api/client';
import type { ScheduledMessage } from '../types';

interface ScheduledMessagesListProps {
  rows: ScheduledMessage[];
  onChanged?: () => void;
}

function statusPill(status: ScheduledMessage['status']): string {
  if (status === 'sent') return 'bg-success-bg text-success';
  if (status === 'failed') return 'bg-error-bg text-error';
  if (status === 'cancelled') return 'bg-canvas-secondary text-ink-secondary';
  return 'bg-info-bg text-info';
}

export function ScheduledMessagesList({ rows, onChanged }: ScheduledMessagesListProps) {
  const [busyId, setBusyId] = useState<string | null>(null);

  async function cancel(id: string) {
    try {
      setBusyId(id);
      await outboundAPI.cancelScheduled(id);
      onChanged?.();
    } catch (err) {
      console.error('Failed to cancel scheduled message:', err);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="rounded-xl border border-edge bg-white p-3 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ink" data-ph-no-mask>Scheduled Messages</h3>
        <p className="text-xs text-ink-secondary" data-ph-no-mask>{rows.length} total</p>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-ink-tertiary" data-ph-no-mask>No scheduled outbound messages yet.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.id} className="rounded-lg border border-edge/70 p-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-ink truncate">
                    {row.contact_name || `Contact #${row.contact_id}`}
                  </p>
                  <p className="text-[11px] text-ink-secondary">
                    {new Date(row.send_at).toLocaleString()}
                  </p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${statusPill(row.status)}`}>
                  {row.status}
                </span>
              </div>

              <p className="mt-2 text-xs text-ink-secondary">{row.content}</p>
              {row.error && <p className="mt-1 text-[11px] text-red-600">{row.error}</p>}

              {row.status === 'pending' && (
                <button
                  onClick={() => void cancel(row.id)}
                  disabled={busyId === row.id}
                  className="mt-2 text-xs px-2 py-1 rounded-md border border-edge bg-canvas hover:bg-canvas-secondary disabled:opacity-50"
                  data-ph-no-mask
                >
                  {busyId === row.id ? 'Cancelling...' : 'Cancel'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default ScheduledMessagesList;
